import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';

interface Infrastructure {
  id: string;
  workspace_id: string;
  element_name: string;
  status: 'not_started' | 'creating' | 'ready' | 'failed';
  created_at: string;
  updated_at: string;
  gitlab_pipeline_id?: number;
  gitlab_pipeline_status?: string;
  gitlab_pipeline_url?: string;
}

interface StatusSummary {
  status_counts: {
    total: number;
    not_started: number;
    creating: number;
    ready: number;
    failed: number;
  };
  elements: {
    not_started: Infrastructure[];
    creating: Infrastructure[];
    ready: Infrastructure[];
    failed: Infrastructure[];
  };
  workspace_id: string;
  workspace_name: string;
  is_ready_for_infrastructure: boolean;
  summary_generated_at: string;
}

interface UseInfrastructureReturn {
  infrastructure: Infrastructure[];
  statusSummary: StatusSummary | null;
  loading: boolean;
  error: string | null;
  isAllReady: boolean;
  refresh: () => Promise<void>;
}

export const useInfrastructure = (workspaceId: string | undefined): UseInfrastructureReturn => {
  const [infrastructure, setInfrastructure] = useState<Infrastructure[]>([]);
  const [statusSummary, setStatusSummary] = useState<StatusSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { get } = useAuthenticatedApi();

  const fetchInfrastructure = useCallback(async () => {
    if (!workspaceId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch infrastructure list and status summary in parallel
      const [infrastructureResponse, summaryResponse] = await Promise.all([
        get(`/workspaces/${workspaceId}/infrastructure`),
        get(`/workspaces/${workspaceId}/infrastructure/status-summary`)
      ]);
      
      setInfrastructure(infrastructureResponse);
      setStatusSummary(summaryResponse);
    } catch (err) {
      console.error('Error fetching infrastructure:', err);
      setError('Failed to load infrastructure data');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, get]);

  useEffect(() => {
    fetchInfrastructure();
  }, [fetchInfrastructure]);

  const isAllReady = infrastructure.length > 0 && 
    statusSummary?.status_counts.ready === infrastructure.length;

  return {
    infrastructure,
    statusSummary,
    loading,
    error,
    isAllReady,
    refresh: fetchInfrastructure
  };
}; 